import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import axios from '../../config/axios';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchEmailAccounts,
  createEmailAccount,
  updateEmailAccount,
  deleteEmailAccount,
  testEmailConnection,
  checkEmailAccount,
  checkAllEmailAccounts,
  clearError,
} from '../../store/slices/emailAccountSlice';
import { EmailAccount, EmailAccountFormData } from '../../types/emailAccount';
import { useModernAlert } from '../../hooks/useModernAlert';

const EmailAccounts: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { accounts, loading, error } = useSelector((state: RootState) => state.emailAccounts);
  const { showSuccess, showError, showWarning, showInfo, AlertComponent } = useModernAlert();

  const [currentTab, setCurrentTab] = useState(0);
  const [processingHistory, setProcessingHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [formData, setFormData] = useState<EmailAccountFormData>({
    account_name: '',
    email_address: '',
    protocol: 'IMAP',
    host: '',
    port: 993,
    username: '',
    password: '',
    use_ssl: true,
    is_active: true,
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<number | null>(null);

  useEffect(() => {
    dispatch(fetchEmailAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      showError(error);
      dispatch(clearError());
    }
  }, [error, showError, dispatch]);

  const handleOpenDialog = (account?: EmailAccount) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        account_name: account.account_name,
        email_address: account.email_address,
        protocol: account.protocol,
        host: account.host,
        port: account.port,
        username: account.username,
        password: '', // Don't populate password
        use_ssl: account.use_ssl,
        is_active: account.is_active,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        account_name: '',
        email_address: '',
        protocol: 'IMAP',
        host: '',
        port: 993,
        username: '',
        password: '',
        use_ssl: true,
        is_active: true,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAccount(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: e.target.type === 'checkbox' ? checked : value,
    }));
  };

  const handleTestConnection = async () => {
    if (!editingAccount) {
      showWarning('Please save the account first before testing');
      return;
    }

    setTestingConnection(true);
    try {
      const result = await dispatch(testEmailConnection(editingAccount.id)).unwrap();
      if (result.success) {
        showSuccess('Connection successful!');
      } else {
        showError(`Connection failed: ${result.message}`);
      }
    } catch (err: any) {
      showError(err || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async () => {
    console.log('Submit clicked, form data:', formData);
    try {
      if (editingAccount) {
        // Update existing account
        const updateData: Partial<EmailAccountFormData> = { ...formData };
        if (!formData.password) {
          delete updateData.password; // Don't update password if not provided
        }
        console.log('Updating account:', editingAccount.id, updateData);
        await dispatch(updateEmailAccount({ id: editingAccount.id, data: updateData })).unwrap();
        showSuccess('Email account updated successfully');
      } else {
        // Create new account
        console.log('Creating new account:', formData);
        await dispatch(createEmailAccount(formData)).unwrap();
        showSuccess('Email account created successfully');
      }
      handleCloseDialog();
    } catch (err: any) {
      console.error('Submit error:', err);
      showError(err || 'Failed to save email account');
    }
  };

  const handleDelete = async () => {
    if (accountToDelete) {
      try {
        await dispatch(deleteEmailAccount(accountToDelete)).unwrap();
        showSuccess('Email account deleted successfully');
      } catch (err: any) {
        showError(err || 'Failed to delete email account');
      }
    }
    setDeleteConfirmOpen(false);
    setAccountToDelete(null);
  };

  const handleCheckAccount = async (id: number) => {
    try {
      await dispatch(checkEmailAccount(id)).unwrap();
      showInfo('Email check started');
    } catch (err: any) {
      showError(err || 'Failed to start email check');
    }
  };

  const handleCheckAll = async () => {
    try {
      await dispatch(checkAllEmailAccounts()).unwrap();
      showInfo('Email check started for all accounts');
    } catch (err: any) {
      showError(err || 'Failed to start email check');
    }
  };
  
  const handleViewHistory = async () => {
    setHistoryDialogOpen(true);
    setLoadingHistory(true);
    try {
      const response = await axios.get('/admin/email-processing-history');
      if (response.data.success) {
        setProcessingHistory(response.data.emails || []);
      }
    } catch (err: any) {
      showError('Failed to load processing history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Email Mailboxes</Typography>
        <Box>
          <Button
            variant="outlined"
            onClick={handleViewHistory}
            sx={{ mr: 2 }}
          >
            View All History
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleCheckAll}
            sx={{ mr: 2 }}
          >
            Check All Now
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Email Account
          </Button>
        </Box>
      </Box>

      {loading && !accounts.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : accounts.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="textSecondary" align="center">
              No email accounts configured. Click "Add Email Account" to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Protocol</TableCell>
                <TableCell>Host:Port</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Checked</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                <TableRow key={account.id}>
                  <TableCell>{account.account_name}</TableCell>
                  <TableCell>{account.email_address}</TableCell>
                  <TableCell>{account.protocol}</TableCell>
                  <TableCell>{`${account.host}:${account.port}`}</TableCell>
                  <TableCell>
                    {account.is_active ? (
                      account.last_error ? (
                        <Tooltip title={account.last_error}>
                          <Chip
                            icon={<ErrorIcon />}
                            label="Error"
                            color="error"
                            size="small"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active"
                          color="success"
                          size="small"
                        />
                      )
                    ) : (
                      <Chip label="Inactive" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(account.last_checked_at)}</TableCell>
                  <TableCell>
                    <Tooltip title="Check Now">
                      <IconButton
                        size="small"
                        onClick={() => handleCheckAccount(account.id)}
                        color="primary"
                      >
                        <PlayArrowIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(account)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setAccountToDelete(account.id);
                          setDeleteConfirmOpen(true);
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAccount ? 'Edit Email Account' : 'Add Email Account'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Gmail IMAP Instructions */}
            {formData.email_address.toLowerCase().includes('gmail.com') && (
              <Alert severity="info">
                <strong>Gmail Setup:</strong>
                <br />
                • Protocol: <strong>IMAP</strong>
                <br />
                • Host: <strong>imap.gmail.com</strong>
                <br />
                • Port: <strong>993</strong>
                <br />
                • Enable 2-Step Verification in Google Account
                <br />
                • Use App Password (not your regular password)
                <br />• Create App Password at:{' '}
                <a
                  href="https://myaccount.google.com/apppasswords"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  myaccount.google.com/apppasswords
                </a>
              </Alert>
            )}

            {/* POP3 Warning */}
            {formData.protocol === 'POP3' && (
              <Alert severity="warning">
                <strong>Note:</strong> POP3 support is limited. We recommend using IMAP for better
                reliability. For Gmail, use: Host: imap.gmail.com, Port: 993, Protocol: IMAP
              </Alert>
            )}
            
            <TextField
              label="Account Name"
              name="account_name"
              value={formData.account_name}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label="Email Address"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
              fullWidth
              required
              type="email"
            />
            <TextField
              label="Protocol"
              name="protocol"
              value={formData.protocol}
              onChange={handleInputChange}
              select
              fullWidth
              required
            >
              <MenuItem value="IMAP">IMAP</MenuItem>
              <MenuItem value="POP3">POP3</MenuItem>
            </TextField>
            <TextField
              label="Host"
              name="host"
              value={formData.host}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="imap.gmail.com"
            />
            <TextField
              label="Port"
              name="port"
              value={formData.port}
              onChange={handleInputChange}
              fullWidth
              required
              type="number"
            />
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              fullWidth
              required
            />
            <TextField
              label={editingAccount ? 'Password (leave blank to keep current)' : 'Password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              fullWidth
              required={!editingAccount}
              type="password"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.use_ssl}
                  onChange={handleInputChange}
                  name="use_ssl"
                />
              }
              label="Use SSL/TLS"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  name="is_active"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {editingAccount && (
            <Button
              onClick={handleTestConnection}
              disabled={testingConnection}
            >
              {testingConnection ? 'Testing...' : 'Test Connection'}
            </Button>
          )}
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAccount ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this email account? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Processing History Dialog */}
      <Dialog 
        open={historyDialogOpen} 
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>All Email Processing History</DialogTitle>
        <DialogContent>
          {loadingHistory ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : processingHistory.length === 0 ? (
            <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
              No processing history found
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Sender</TableCell>
                    <TableCell>Matched User</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Invoices</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {processingHistory.map((email: any) => (
                    <TableRow key={email.id}>
                      <TableCell>{new Date(email.created_at).toLocaleString()}</TableCell>
                      <TableCell>{email.account_name}</TableCell>
                      <TableCell>{email.sender_email}</TableCell>
                      <TableCell>
                        {email.user_email || '-'}
                        {email.first_name && ` (${email.first_name} ${email.last_name})`}
                      </TableCell>
                      <TableCell>{email.message_subject || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={email.processing_status || 'pending'} 
                          color={
                            email.processing_status === 'completed' ? 'success' :
                            email.processing_status === 'skipped' ? 'warning' : 
                            email.processing_status === 'failed' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {email.invoices_created && email.invoices_created.length > 0 ? (
                          <Link href={`/invoices/${email.invoices_created[0]}`}>
                            {email.invoices_created.length} invoice(s)
                          </Link>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Alert Component */}
      {AlertComponent}
    </Box>
  );
};

export default EmailAccounts;

